/**
 * AgencyDetailsModal — appears when an athlete taps their pending
 * agent-invite banner to inspect who wants to represent them before
 * approving.  Shows:
 *   • Agency identity (logo, name, agent name, bio)
 *   • Trust signals (roster size, commission %, member since)
 *   • Links (website, IG, Twitter, LinkedIn)
 *   • Roster preview — other athletes already on this agency, click-
 *     through to their public network profile.
 *   • Inline Approve / Decline actions so the athlete never has to
 *     bounce back to the banner.
 *
 * Sourced from `GET /api/me/athlete/agent-invites/:id/details`.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import {
  X,
  BadgeCheck,
  Globe,
  Instagram,
  Twitter,
  Linkedin,
  Users,
  Percent,
  Calendar,
  Loader2,
  Check,
  ShieldCheck,
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

function normalizeUrl(u) {
  if (!u) return "";
  const s = String(u).trim();
  if (!s) return "";
  if (s.startsWith("http")) return s;
  if (s.startsWith("@")) return `https://instagram.com/${s.slice(1)}`;
  return `https://${s.replace(/^\/+/, "")}`;
}

function memberSinceLabel(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
  } catch {
    return null;
  }
}

export default function AgencyDetailsModal({ open, inviteId, onClose, onApproved, onDeclined }) {
  const [state, setState] = useState({ loading: true, data: null, error: "" });
  const [decidingAction, setDecidingAction] = useState(""); // "approve" | "reject"

  useEffect(() => {
    if (!open || !inviteId) return;
    setState({ loading: true, data: null, error: "" });
    axios
      .get(`${API}/api/me/athlete/agent-invites/${inviteId}/details`, { withCredentials: true })
      .then((r) => setState({ loading: false, data: r.data, error: "" }))
      .catch((e) => setState({ loading: false, data: null, error: e?.response?.data?.detail || "Could not load agency details" }));
  }, [open, inviteId]);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);

  const decide = async (action) => {
    if (!inviteId) return;
    setDecidingAction(action);
    try {
      const r = await axios.post(
        `${API}/api/me/athlete/agent-invites/${action}`,
        { invite_id: inviteId },
        { withCredentials: true },
      );
      if (action === "approve") {
        toast.success(`${r.data?.agent_name || "Agent"} is now your agent`);
        onApproved?.(inviteId);
      } else {
        toast.success("Invite declined");
        onDeclined?.(inviteId);
      }
      onClose?.();
    } catch (err) {
      // iter231 · Surface the real backend reason instead of a
      // generic toast. The most common approve-failure is a 402
      // "agent_roster_capacity_reached" — Isabelle sees "Could not
      // update invite" and has no idea it's actually the agent's
      // plan capping her out. Now she sees "This agent's roster is
      // full — they need to upgrade before adding you." and can
      // ping the agent to upgrade.
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : detail?.message || "Could not update invite";
      toast.error(msg, { duration: 6000 });
    } finally {
      setDecidingAction("");
    }
  };

  if (!open) return null;

  const agency = state.data?.agency || {};
  const roster = state.data?.roster || [];
  const accent = agency.accent_color || "#F59E0B";
  const commissionPct = Math.round((agency.commission_rate || 0.15) * 100);
  const memberSince = memberSinceLabel(agency.member_since);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center pt-6 sm:pt-16 px-4 bg-black/80 backdrop-blur-xl"
      onClick={onClose}
      data-testid="agency-details-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl rounded-3xl border border-white/10 bg-gradient-to-b from-white/[0.06] to-black/70 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.9)] overflow-hidden max-h-[92vh] flex flex-col"
        style={{ boxShadow: `0 0 0 1px ${accent}30, 0 40px 80px -20px rgba(0,0,0,0.9)` }}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-white/10 shrink-0">
          <ShieldCheck className="w-4 h-4" style={{ color: accent }} />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.32em] font-bold" style={{ color: accent }}>Agency profile</p>
            <p className="text-xs text-white/50 mt-0.5">Nothing happens until you approve.</p>
          </div>
          <button
            onClick={onClose}
            data-testid="agency-details-close"
            className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition flex items-center justify-center"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {state.loading ? (
            <div className="py-16 flex justify-center" data-testid="agency-details-loading">
              <Loader2 className="w-5 h-5 animate-spin text-white/40" />
            </div>
          ) : state.error ? (
            <div className="p-8 text-center" data-testid="agency-details-error">
              <p className="text-sm text-red-300">{state.error}</p>
            </div>
          ) : (
            <div className="p-6 space-y-6">
              {/* Identity block */}
              <div className="flex items-start gap-4">
                {agency.link_photo || agency.agency_logo || agency.photo ? (
                  <img
                    src={agency.link_photo || agency.agency_logo || agency.photo}
                    alt=""
                    className="w-16 h-16 rounded-2xl object-cover ring-2"
                    style={{ boxShadow: `0 0 0 2px ${accent}40` }}
                  />
                ) : (
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-2xl font-black text-black" style={{ background: accent }}>
                    {(agency.agency_name || "A").charAt(0)}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-[0.28em] font-bold" style={{ color: accent }}>Agent request</p>
                  <h2 className="font-display text-2xl uppercase tracking-tight text-white leading-tight mt-1 flex items-center gap-2">
                    <span className="truncate">{agency.agency_name}</span>
                    <BadgeCheck className="w-5 h-5 shrink-0" style={{ color: accent }} />
                  </h2>
                  {agency.agent_name && (
                    <p className="text-sm text-white/70 mt-1">Rep: <span className="text-white font-semibold">{agency.agent_name}</span></p>
                  )}
                </div>
              </div>

              {/* Bio */}
              {agency.bio && (
                <p className="text-sm text-white/80 leading-relaxed" data-testid="agency-details-bio">
                  {agency.bio}
                </p>
              )}

              {/* Trust chips */}
              <div className="grid grid-cols-3 gap-2">
                <TrustChip icon={Users}    label="Roster"     value={agency.roster_count ?? 0} accent={accent} testid="agency-chip-roster" />
                <TrustChip icon={Percent}  label="Commission" value={`${commissionPct}%`}      accent={accent} testid="agency-chip-commission" />
                <TrustChip icon={Calendar} label="Since"      value={memberSince || "—"}       accent={accent} testid="agency-chip-since" />
              </div>

              {/* Links */}
              {(agency.website || agency.instagram || agency.twitter || agency.linkedin) && (
                <div className="space-y-2">
                  <p className="text-[10px] uppercase tracking-[0.28em] font-bold text-white/40">Links</p>
                  <div className="flex flex-wrap gap-2">
                    {agency.website   && <LinkPill icon={Globe}     label="Website"   href={normalizeUrl(agency.website)}   testid="agency-link-website" />}
                    {agency.instagram && <LinkPill icon={Instagram} label="Instagram" href={normalizeUrl(agency.instagram)} testid="agency-link-ig" />}
                    {agency.twitter   && <LinkPill icon={Twitter}   label="Twitter"   href={normalizeUrl(agency.twitter)}   testid="agency-link-tw" />}
                    {agency.linkedin  && <LinkPill icon={Linkedin}  label="LinkedIn"  href={normalizeUrl(agency.linkedin)}  testid="agency-link-li" />}
                  </div>
                </div>
              )}

              {/* Roster preview */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] uppercase tracking-[0.28em] font-bold text-white/40">
                    Also on this roster{roster.length ? ` · ${roster.length}` : ""}
                  </p>
                </div>
                {roster.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-white/10 p-5 text-center" data-testid="agency-roster-empty">
                    <p className="text-xs text-white/50">You&apos;d be their first approved athlete — that means direct focus.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="agency-roster-list">
                    {roster.slice(0, 8).map((a) => (
                      <Link
                        key={a.slug}
                        to={`/network/${a.slug}`}
                        onClick={onClose}
                        className="flex items-center gap-3 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] ring-1 ring-white/10 hover:ring-white/25 px-3 py-2.5 transition"
                        data-testid={`agency-roster-${a.slug}`}
                      >
                        {a.photo ? (
                          <img src={a.photo} alt="" className="w-10 h-10 rounded-full object-cover shrink-0" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-white/10 shrink-0" />
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-bold text-white truncate">{a.name}</p>
                          <p className="text-[11px] text-white/50 truncate">
                            {a.sport || "Athlete"}
                            {a.team ? ` · ${a.team}` : a.school ? ` · ${a.school}` : ""}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
                {roster.length > 8 && (
                  <p className="text-[11px] text-white/40 text-center mt-2">+ {roster.length - 8} more</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        {!state.loading && !state.error && (
          <div className="border-t border-white/10 px-6 py-4 flex flex-col gap-2 shrink-0">
            {agency.capacity_reached && (
              <p
                className="text-[11px] text-amber-300/90 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 leading-snug"
                data-testid="agency-details-capacity-warning"
              >
                <ShieldCheck className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5" />
                {agency.agency_name}&rsquo;s roster is full ({agency.roster_count}
                {agency.roster_capacity ? ` / ${agency.roster_capacity}` : ""}). They&rsquo;ll need to upgrade their plan before they can add you — reach out and let them know.
              </p>
            )}
            <div className="flex items-center gap-2">
              <button
                onClick={() => decide("reject")}
                disabled={!!decidingAction}
                data-testid="agency-details-decline"
                className="inline-flex items-center justify-center gap-1.5 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 text-white/70 text-xs uppercase tracking-widest font-bold px-4 py-2.5 transition disabled:opacity-50"
              >
                {decidingAction === "reject" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
                Decline
              </button>
              <button
                onClick={() => decide("approve")}
                disabled={!!decidingAction || !!agency.capacity_reached}
                data-testid="agency-details-approve"
                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full text-black font-bold text-xs uppercase tracking-widest px-4 py-2.5 transition disabled:opacity-50"
                style={{
                  background: accent,
                  boxShadow: `0 0 0 1px ${accent}80, 0 12px 28px -8px ${accent}80`,
                }}
              >
                {decidingAction === "approve" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                {agency.capacity_reached ? "Roster full · ask them to upgrade" : "Approve · This is my agent"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const TrustChip = ({ icon: Icon, label, value, accent, testid }) => (
  <div className="rounded-xl bg-white/[0.04] ring-1 ring-white/10 px-3 py-2.5 flex items-center gap-2.5" data-testid={testid}>
    <Icon className="w-4 h-4 shrink-0" style={{ color: accent }} />
    <div className="min-w-0">
      <p className="text-[9px] uppercase tracking-widest text-white/40">{label}</p>
      <p className="text-sm font-bold text-white truncate">{value}</p>
    </div>
  </div>
);

const LinkPill = ({ icon: Icon, label, href, testid }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testid}
    className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.06] hover:bg-white/[0.12] border border-white/10 hover:border-white/25 px-3 py-1.5 text-[11px] uppercase tracking-widest font-bold text-white/80 hover:text-white transition"
  >
    <Icon className="w-3 h-3" />
    {label}
  </a>
);
